<template>
  <div class="sidebar-settings">
    <div class="sidebar-settings-title">Style &amp; Animation</div>

    <opacity-settings :element="element"></opacity-settings>

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="sidebar-form-label">Font</label>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <div class="dropdown fancy-dropdown">
            <button
              class="dropdown-btn select"
              data-toggle="dropdown"
              :class="'font-' + getFontName.cssClass">
              {{ getFontName.name }}
            </button>
            <ul class="dropdown-menu">
              <li v-for="font in fonts">
                <a
                  href="#"
                  @click.prevent="setFont(font.font)"
                  class="dropdown-menu-item"
                  :class="[
                    'font-' + font.cssClass,
                    { selected: element.styles.fontFamily === font.font },
                  ]">
                  {{ font.name }}
                  <span
                    v-if="element.styles.fontFamily === font.font"
                    class="fa-solid fa-check"></span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-6 settings-col-xs">
          <validation-field as="input"
            type="number"
            name="fontSize"
            @input.stop.prevent="textChange"
            rules="required|numeric|min:0"
            min="0"
            :class="{ 'is-invalid': validationErrors['fontSize'] }"
            v-model.number="element.styles.fontSize" />
          <label class="sidebar-input-sub-label">Size</label>
        </div>
        <div class="settings-col-6 settings-col-xs">
          <validation-field as="input"
            type="number"
            name="letterSpacing"
            @input.stop.prevent="textChange"
            rules="required|numeric|min:0"
            min="0"
            :class="{ 'is-invalid': validationErrors['letterSpacing'] }"
            v-model.number="element.styles.letterSpacing" />
          <label class="sidebar-input-sub-label">Letter spacing</label>
        </div>
      </div>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <div class="colorpicker">
            <colorpicker :parent="element.styles" field="color"></colorpicker>
          </div>
          <label class="sidebar-input-sub-label">Color</label>
        </div>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="sidebar-form-label">Text styles</label>

      <div class="settings-row settings-row-xs text-styles">
        <div class="settings-col-12 settings-col-xs simple">
          <button
            class="btn btn-sm btn-icon"
            :class="getFontWeightBtnClass"
            @click="setFontWeight">
            <span class="fa-solid fa-bold"></span>
          </button>
          <button
            class="btn btn-sm btn-icon"
            :class="getFontStyleBtnClass"
            @click="setFontStyle">
            <span class="fa-solid fa-italic"></span>
          </button>
        </div>
      </div>

      <label class="sidebar-form-label">Text options</label>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <div class="btn-group text-decorations">
            <button
              type="button"
              v-for="style in textDecorations"
              @click="setDecorationStyle(style.value)"
              :class="[
                { selected: element.styles.textDecoration === style.value },
              ]"
              class="btn btn-sm">
              <span class="icon" :class="'icon-' + style.icon"></span>
            </button>
          </div>
          <label class="sidebar-input-sub-label">Decoration</label>
        </div>
      </div>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <div class="btn-group text-transforms">
            <button
              type="button"
              v-for="style in textTransforms"
              @click="setTransformStyle(style.value)"
              :class="[
                { selected: element.styles.textTransform === style.value },
              ]"
              class="btn btn-sm">
              <span class="icon" :class="'icon-' + style.icon"></span>
            </button>
          </div>
          <label class="sidebar-input-sub-label">Transform</label>
        </div>
      </div>

      <label class="sidebar-form-label">Text alignment</label>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <div class="btn-group">
            <button
              type="button"
              v-for="align in alignments"
              @click="setAlignment(align)"
              :class="[{ selected: element.styles.textAlign === align }]"
              class="btn btn-sm">
              <span class="fa-solid" :class="'fa-align-' + align"></span>
            </button>
          </div>
        </div>
      </div>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <div class="btn-group vertical-alignments">
            <button
              type="button"
              v-for="align in verticalAlignments"
              :key="align"
              @click="setVerticalAlignment(align)"
              :class="[{ selected: element.verticalAlign === align }]"
              class="btn btn-sm">
              <span :class="`fak fa-text-align-${align}`"></span>
            </button>
          </div>
        </div>
      </div>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <label class="checkbox"
            >Shrink text to fit
            <input
              type="checkbox"
              @change="resizeText"
              v-model="element.fitText" />
            <div class="checkmark">
              <span class="checkmark-tick fa-solid fa-check"></span>
            </div>
          </label>
        </div>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="checkbox header-checkbox"
        >Text shadow
        <input type="checkbox" v-model="element.textShadow" />
        <div class="checkmark">
          <span class="checkmark-tick fa-solid fa-check"></span>
        </div>
      </label>

      <div v-if="element.textShadow" class="settings-row settings-row-xs">
        <div class="settings-col-3 settings-col-xs">
          <validation-field as="input"
            type="number"
            name="textShadowX"
            rules="required|integer"
            :class="{ 'is-invalid': validationErrors['textShadowX'] }"
            v-model.number="element.textShadowX" />
          <label class="sidebar-input-sub-label">X</label>
        </div>
        <div class="settings-col-3 settings-col-xs">
          <validation-field as="input"
            type="number"
            name="textShadowY"
            rules="required|integer"
            :class="{ 'is-invalid': validationErrors['textShadowY'] }"
            v-model.number="element.textShadowY" />
          <label class="sidebar-input-sub-label">Y</label>
        </div>
        <div class="settings-col-3 settings-col-xs">
          <validation-field as="input"
            type="number"
            name="textShadowBlur"
            rules="required|integer"
            :class="{ 'is-invalid': validationErrors['textShadowBlur'] }"
            v-model.number="element.textShadowBlur" />
          <label class="sidebar-input-sub-label">Blur</label>
        </div>
        <div class="settings-col-3 settings-col-xs colorpicker">
          <colorpicker :parent="element" field="textShadowColor"></colorpicker>
          <label class="sidebar-input-sub-label">Color</label>
        </div>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="checkbox header-checkbox"
        >Background color
        <input type="checkbox" v-model="element.background" />
        <div class="checkmark">
          <span class="checkmark-tick fa-solid fa-check"></span>
        </div>
      </label>

      <div v-if="element.background" class="settings-row settings-row-xs">
        <label class="radio inline-radio" for="solid"
          >Solid
          <input
            type="radio"
            id="solid"
            value="solid"
            v-model="element.fillStyle" />
          <div class="checkmark"></div>
        </label>
        <label class="radio inline-radio" for="gradient"
          >Gradient
          <input
            type="radio"
            id="gradient"
            value="gradient"
            v-model="element.fillStyle" />
          <div class="checkmark"></div>
        </label>

        <div v-if="element.fillStyle == 'solid'" class="colorpicker">
          <colorpicker :parent="element" field="backgroundColor"></colorpicker>
        </div>

        <div v-else class="settings-row settings-row-xs">
          <div class="settings-col-6 settings-col-xs">
            <div class="input-group">
              <validation-field as="input"
                type="number"
                name="gradientDirection"
                rules="required|numeric"
                :class="{ 'is-invalid': validationErrors['gradientDirection'] }"
                v-model.number="element.gradientDirection" />
              <span class="input-group-addon symbol">°</span>
            </div>
          </div>
          <div class="settings-col-3 settings-col-xs colorpicker">
            <colorpicker
              :parent="element"
              field="gradientColorOne"></colorpicker>
          </div>
          <div class="settings-col-3 settings-col-xs colorpicker">
            <colorpicker
              :parent="element"
              field="gradientColorTwo"></colorpicker>
          </div>
        </div>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <animation-settings :element="element"></animation-settings>
  </div>
</template>

<script>
import EventBus from '../../utils/event-bus';
import SettingsMixins from '../../mixins/settings-mixins';

import AnimationSettings from './Animation.vue';
import OpacitySettings from './Opacity.vue';
import BoxShadowSettings from './BoxShadow.vue';

export default {
  mixins: [SettingsMixins],
  props: ['element'],
  components: {
    'animation-settings': AnimationSettings,
    'opacity-settings': OpacitySettings,
    'box-shadow-settings': BoxShadowSettings,
  },
  data: function () {
    return {
      fonts: [
        {
          cssClass: 'default',
          name: 'System UI Font',
          font: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
        },
        { cssClass: 'roboto', name: 'Roboto', font: 'Roboto' },
        {
          cssClass: 'robotocondensed',
          name: 'Roboto Condensed',
          font: 'Roboto Condensed',
        },
        { cssClass: 'opensans', name: 'Open Sans', font: 'Open Sans' },
        { cssClass: 'lato', name: 'Lato', font: 'Lato' },
        { cssClass: 'raleway', name: 'Raleway', font: 'Raleway' },
        { cssClass: 'montserrat', name: 'Montserrat', font: 'Montserrat' },
        {
          cssClass: 'librebaskerville',
          name: 'Libre Baskerville',
          font: 'Libre Baskerville',
        },
        {
          cssClass: 'merriweather',
          name: 'Merriweather',
          font: 'Merriweather',
        },
      ],
      textDecorations: [
        { value: 'none', icon: 'text-normal' },
        { value: 'underline', icon: 'text-underline' },
        { value: 'line-through', icon: 'text-strikethrough' },
      ],
      textTransforms: [
        { value: 'none', icon: 'text-normal' },
        { value: 'uppercase', icon: 'text-uppercase' },
        { value: 'lowercase', icon: 'text-lowercase' },
      ],
      alignments: ['left', 'center', 'right', 'justify'],
      verticalAlignments: ['top', 'middle', 'bottom'],
    };
  },
  computed: {
    getFontName: function () {
      var self = this;

      return self.fonts.filter(function (font) {
        return font.font === self.element.styles.fontFamily;
      })[0];
    },
    getFontWeightBtnClass: function () {
      return this.element.styles.fontWeight === 'bold'
        ? 'btn-style-a'
        : 'btn-style-d';
    },
    getFontStyleBtnClass: function () {
      return this.element.styles.fontStyle === 'italic'
        ? 'btn-style-a'
        : 'btn-style-d';
    },
  },
  methods: {
    textChange: function () {
      var self = this;
      this.$nextTick(function () {
        self.resizeText();
      });
    },
    setAlignment: function (style) {
      this.element.styles.textAlign = style;
    },
    setVerticalAlignment: function (style) {
      this.element.verticalAlign = style;
    },
    setFontWeight: function () {
      if (this.element.styles.fontWeight === 'normal') {
        this.element.styles.fontWeight = 'bold';
      } else {
        this.element.styles.fontWeight = 'normal';
      }
    },
    setFontStyle: function () {
      if (this.element.styles.fontStyle === 'normal') {
        this.element.styles.fontStyle = 'italic';
      } else {
        this.element.styles.fontStyle = 'normal';
      }
    },
    setDecorationStyle: function (style) {
      this.element.styles.textDecoration = style;
    },
    setTransformStyle: function (style) {
      this.element.styles.textTransform = style;
    },
    setFont: function (font) {
      this.element.styles.fontFamily = font;
    },
    resizeText: function () {
      EventBus.emit('resize-text');
    },
  },
};
</script>
